'use client';

import React from 'react';
import ImagePlaceholder from './ImagePlaceholder';
import styles from './ProgramsCarousel.module.css';

export type CarouselItem =
  | string
  | { image: string; logo?: string; label: string };

type Props = {
  items: CarouselItem[];
  /** Pixels-per-second scroll speed. Lower = slower. */
  speed?: number;
  ariaLabel: string;
};

/**
 * Apple-style horizontal marquee. Content is duplicated so the loop is
 * seamless; hover pauses the scroll. Items can be image URLs OR plain
 * strings (rendered as labelled placeholders until images are provided).
 */
const ProgramsCarousel: React.FC<Props> = ({
  items,
  speed = 40,
  ariaLabel,
}) => {
  // Duplicate list once so the -50% transform lands exactly where the
  // first copy started, producing a seamless loop.
  const doubled = React.useMemo(() => [...items, ...items], [items]);

  const totalWidthPx = items.length * 380 + (items.length - 1) * 24;
  const duration = Math.max(20, Math.round(totalWidthPx / speed));

  return (
    <div className={styles.frame} aria-label={ariaLabel}>
      <div
        className={styles.track}
        style={
          {
            ['--scroll-duration' as string]: `${duration}s`,
          } as React.CSSProperties
        }
      >
        {doubled.map((item, idx) => {
          const isObject = typeof item === 'object';
          const label = isObject ? item.label : item;
          const image = isObject ? item.image : undefined;
          const ariaHidden = idx >= items.length;

          const logo = isObject ? item.logo : undefined;

          return (
            <div
              className={styles.cardItem}
              key={`${label}-${idx}`}
              aria-hidden={ariaHidden}
            >
              {image ? (
                <>
                  <img
                    className={styles.cardImage}
                    src={image}
                    alt={label}
                    loading="lazy"
                  />
                  {logo ? (
                    <img className={styles.cardLogo} src={logo} alt={label} />
                  ) : null}
                </>
              ) : (
                <ImagePlaceholder
                  label={label}
                  ratio="4/3"
                  dark
                  fill
                  radius={0}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgramsCarousel;
